import { useCallback, useEffect, useState } from 'react'
import { Card, Col, Form, Row } from 'react-bootstrap'
import MenuItem from '@mui/material/MenuItem'
import { useDispatch } from 'react-redux'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** STYLE */
import useStyles from 'features/setting/default-users/experts/style'

/** API */
import ExpertApi from 'api/setting/default-users/expert.api'

/** UTILS || SLICE */
import { submitModal, unSubmitModal, showModal, resetModal } from 'app/slice/modal.slice'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import { swalActive } from 'component/Alert/Swal'
import ButtonCustom from 'component/Button/ButtonCustom'
import HeaderCard from 'component/CardCustom/HeaderCard'
import InputTextField from 'component/Input/InputTextField'
import InputTextSearch from 'component/Input/InputTextSearch'
import ModalCustom from 'component/ModalCustom/ModalCustom'
import TableCustom from 'component/Table/TableCustom'
import { notiSuccess, notiError } from 'component/notifications/notifications'
import TableRowCommon from 'component/Table/TableRowCommon'
import FilterSelect from 'component/Select/FilterSelect'

const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}
const initStateErrorMessage = {
  EXPERT_NAME: ``,
  IS_DUPLICATE_EXPERT_NAME: ``,
  EXPERT_NAME_STRING_BASE: ``,
  EXPERT_NAME_STRING_EMPTY: ``,
  EXPERT_NAME_MAXIMUM_LENGTH: ``,
  EXPERT_NAME_ANY_REQUIRED: ``,

  EXPERT_NAME_EN: ``,
  IS_DUPLICATE_EXPERT_NAME_EN: ``,
  EXPERT_NAME_EN_STRING_BASE: ``,
  EXPERT_NAME_EN_STRING_EMPTY: ``,
  EXPERT_NAME_EN_MAXIMUM_LENGTH: ``,
  EXPERT_NAME_EN_ANY_REQUIRED: ``
}
export default function Experts() {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [experts, setExperts] = useState([])

  const [expertId, setExpertId] = useState(Number)
  const [expertName, setExpertName] = useState('')
  const [expertNameEn, setExpertNameEn] = useState('')
  const [expertStatus, setExpertStatus] = useState('')

  const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')

  const loadData = useCallback(async () => {

    let condition: any = {}
    if (page) condition = { ...condition, page: page }
    if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
    if (search) condition = { ...condition, search: search }
    if (sortBy) condition = { ...condition, sortBy: sortBy }
    if (sortType) condition = { ...condition, sortType: sortType }
    if (status) condition = { ...condition, status: status === 'ALL' ? '' : status }

    const res = await ExpertApi.findAll(condition)
    if (res.status === 200) {
      setRowCount(res.headers['x-total'])
      setExperts(res.data)
    }
  }, [page, pageLimit, search, sortBy, sortType, status])

  useEffect(() => {
    loadData()
  }, [loadData])

  const submit = () => {
    if (!expertName) return setErrorMessage({ ...errorMessage, ...{ EXPERT_NAME: t('EXPERT.MESSAGE.EXPERT_NAME') } })
    dispatch(submitModal())

    ExpertApi.create(expertName, expertNameEn)
      .then((res: any) => {
        if (res.status !== undefined && res.status === 201) {
          notiSuccess(t('EXPERT.MESSAGE.SUCCESS.CREATE'), '', null, null)
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`EXPERT.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`EXPERT.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      })
  }

  const submitEdit = () => {
    if (!expertName) return setErrorMessage({ ...errorMessage, ...{ EXPERT_NAME: t(`EXPERT.MESSAGE.EXPERT_NAME`) } })

    dispatch(submitModal())
    ExpertApi.update(expertId, {
      expertName,
      expertNameEn,
      status: expertStatus
    })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 200) {
          notiSuccess(t(`EXPERT.MESSAGE.SUCCESS.UPDATE`), '', null, null)
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = res.response.data

          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`EXPERT.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`EXPERT.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      })
  }

  const clearErrorMessage = () => {
    setErrorMessage(initStateErrorMessage)
  }
  const resetForm = () => {
    setExpertId(0)
    setExpertName('')
    setExpertNameEn('')
    setExpertStatus('')
    clearErrorMessage()
  }

  const onActive = (id: number, valueStatus: string) => {
    swalActive(
      `<p class="mb-0 mx-auto" style="max-width: 325px">${t('EXPERT.ALERT.CONFIRM_STATUS')}</p>`,
      null, (res: any) => {
        if (res) {
          ExpertApi.updateStatus(id, valueStatus)
            .then((resp) => {
              notiSuccess(t('EXPERT.MESSAGE.SUCCESS.UPDATE_STATUS'))
              loadData()
            })
            .catch((e) => {
              notiError(t('EXPERT.MESSAGE.ERROR'))
            })
        }
      }, null, t('BUTTON.SUBMIT'), t('BUTTON.CANCEL'))
  }
  const onDelete = (data: any) => {
    const { expertId: expId, expertName: expName, expertNameEn: expNameEn } = data

    swalActive(
      `<p class="mb-0 mx-auto" style="max-width: 325px">${t('EXPERT.ALERT.CONFIRM_DELETE')}</p>`,
      `<p class="mb-0 mx-auto" style="max-width: 325px">${t('EXPERT.ALERT.CONFIRM_DELETE_', {
        expertName: expName,
        expertNameEn: expNameEn ? ' (' + expNameEn + ')' : ''
      })}</p>`,
      (res: any) => {
        if (res) {
          ExpertApi.remove(expId)
            .then((resp) => {
              notiSuccess(t('EXPERT.MESSAGE.SUCCESS.DELETE'))
              loadData()
            })
            .catch((e) => {
              notiError(t('EXPERT.MESSAGE.ERROR'))
            })
        }
      }, null, t('BUTTON.SUBMIT'), t('BUTTON.CANCEL')
    )
  }

  const onEdit = (data: any) => {
    const { expertId: expId, expertName: expName, expertNameEn: expNameEn, status: expStatus } = data
    setExpertId(expId)
    setExpertName(expName)
    setExpertNameEn(expNameEn)
    setExpertStatus(expStatus)
    dispatch(showModal())
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }
  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }
  const headCells = [
    { id: 'no', disablePadding: false, label: '#' },
    { id: 'expertName', disablePadding: false, label: t('EXPERT.TABLE.CELL.EXPERT_NAME') },
    { id: 'expertNameEn', disablePadding: false, label: t('EXPERT.TABLE.CELL.EXPERT_NAME_EN') },
    { id: 'status', disablePadding: false, label: t('EXPERT.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('EXPERT.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { expertId: expId, expertName: expName, expertNameEn: expNameEn, updatedBy, updatedAt, status: expStatus } = objData
    const statusBtnActive = expStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = expStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled
    const objRenderData = {
      key: expId,
      id: expId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: expName },
        { option: 'TEXT', align: 'left', label: expNameEn },
        { option: 'STATUS', align: 'center', label: expStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => onActive(Number(objRenderData.id), 'ACTIVE')}
        oninactive={() => onActive(Number(objRenderData.id), 'INACTIVE')}
        onedit={() => onEdit(objRenderData.obj)}
        ondelete={() => onDelete(objRenderData.obj)}
      />
    )
  }

  return (
    <div className={classes.root}>
      <Card className="border-0 h-100">
        <HeaderCard text={t('EXPERT.TITLE')} />
        <Card.Body>
          <Card.Title className={'mb-0 pl-xl-3'}>
            <Row className="align-items-center">
              <Col md={8}>
                <Row className="flax-row">
                  <Col sm={12} md={6} xl={4} className="pt-2">
                    <InputTextSearch
                      keyInput={'search'}
                      label={t('INPUT.SEARCH')}
                      value={search}
                      onchange={(event) => {
                        setSearch(event.target.value)
                        setPage(1)
                      }}
                      onClear={(event) => {
                        setSearch('')
                        setPage(1)
                      }}
                    />
                  </Col>
                  <Col sm={12} md={6} xl={4} className="pl-md-0 pt-2">
                    <FilterSelect
                      onchange={(event) => {
                        const value = event.target.value
                        if (value) {
                          handleChangeStatus(value)
                          setPage(1)
                        }
                      }}
                      renderValue={() => `${t('STATUS.LABEL')}: ${t(`STATUS.${status}`)}`}
                      label={t('STATUS.LABEL')}
                      selectId="select-status"
                      value={status}
                      labelId="label-status"
                      options={[
                        <MenuItem key="1" value="ALL">
                          {t('STATUS.ALL')}
                        </MenuItem>,
                        <MenuItem key="2" value="ACTIVE">
                          {t('STATUS.ACTIVE')}
                        </MenuItem>,
                        <MenuItem key="3" value="INACTIVE">
                          {t('STATUS.INACTIVE')}
                        </MenuItem>
                      ]}
                    />
                  </Col>
                </Row>
              </Col>
              <Col md={4} className="mt-2 mt-md-auto">
                <div className="pr-xl-2">
                  <ButtonCustom mode="add" disabled={permissions.isCreate.disabled} onClick={() => dispatch(showModal())} textButton={t('EXPERT.BUTTON.ADD')} className="w-auto ml-auto d-flex mt-auto" />
                </div>
              </Col>
            </Row>
          </Card.Title>

          <div className={'mt-3 pl-xl-3 pr-xl-2'}>
            <TableCustom
              page={page}
              pageLimit={pageLimit}
              sortType={sortType}
              sortBy={sortBy}
              onSort={onRequestSort}
              setPageLimit={handleChangeRowsPerPage}
              setPage={handleChangePage}
              rowCount={rowCount}
              headCells={headCells}
              rowsData={experts.map((val, i) => {
                return renderData(val, i)
              })}
            />
          </div>
        </Card.Body>
      </Card>

      <ModalCustom
        title={expertId ? t('EXPERT.TITLE_UPDATE') : t('EXPERT.TITLE_CREATE')}
        component={
          <div className="pb-2">
            {expertId ? (
              <div>
                <Form className="mx-0 mt-0 mb-3 p-0 d-inline-flex w-100">
                  <Form.Label className=" mr-auto">{t('INPUT.LABEL_STATUS')}</Form.Label>
                  <Form.Check type="switch" id="custom-switch" onChange={() => setExpertStatus(expertStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')} checked={expertStatus === 'ACTIVE' ? true : false} style={{ marginLeft: 'auto' }} />
                </Form>
              </div>
            ) : (
              ''
            )}

            <InputTextField
              onchange={(event) => {
                setExpertName(event.target.value)
                clearErrorMessage()
              }}
              value={expertName}
              label={t('EXPERT.INPUT.EXPERT_NAME')}
              helperText={
                errorMessage.EXPERT_NAME || errorMessage.IS_DUPLICATE_EXPERT_NAME || errorMessage.EXPERT_NAME_STRING_BASE || errorMessage.EXPERT_NAME_STRING_EMPTY || errorMessage.EXPERT_NAME_MAXIMUM_LENGTH || errorMessage.EXPERT_NAME_ANY_REQUIRED
              }
              required={true}
              style={{ marginBottom: '1rem' }}
            />
            <InputTextField
              key="expertNameEn"
              onchange={(event) => {
                setExpertNameEn(event.target.value)
                clearErrorMessage()
              }}
              value={expertNameEn}
              helperText={
                errorMessage.EXPERT_NAME_EN ||
                errorMessage.IS_DUPLICATE_EXPERT_NAME_EN ||
                errorMessage.EXPERT_NAME_EN_STRING_BASE ||
                errorMessage.EXPERT_NAME_EN_STRING_EMPTY ||
                errorMessage.EXPERT_NAME_EN_MAXIMUM_LENGTH ||
                errorMessage.EXPERT_NAME_EN_ANY_REQUIRED
              }
              label={t('EXPERT.INPUT.EXPERT_NAME_EN')}
              required={false}
            />
          </div>
        }
        onReset={resetForm}
        onSubmit={expertId ? submitEdit : submit}
        textBtnCancel={t('EXPERT.BUTTON.CANCEL')}
        textBtnConfirm={t('EXPERT.BUTTON.SAVE')}
      />
    </div>
  )
}
